import { useState } from "react";
import { Box, Button, Card, Typography, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Loading from "../../components/loading/loading";
import ErrorNotification from "../../components/error-notification/error-notification";
import Notification from "../../components/notification/notification";
import { forgotPasswordRequest } from "../../utils/APICalls";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const onUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(event.target.value);
    setUsernameError(false);
  };

  const handleResetPassword = async () => {
    if (username.trim() === "") {
      setUsernameError(true);
      return;
    }
    try {
      setShowLoading(true);
      const response = await forgotPasswordRequest({ username });
      if (response?.status === 200 || response?.status === 204) {
        setSuccessMessage(
          "If the account exists, password reset instructions will be emailed."
        );
        setShowSuccessNotification(true);
        setEmailSent(true);
      } else {
        const errorMsg =
          response?.data?.message ||
          response?.data?.detail ||
          "Failed to process request. Please try again.";
        setErrorMessage(errorMsg);
        setShowErrorNotification(true);
      }
    } catch (e) {
      setErrorMessage("Failed to process request. Please try again.");
      setShowErrorNotification(true);
    } finally {
      setShowLoading(false);
    }
  };

  return (
    <Box className="backgroud-layout">
      <Box className="signin-layout">
        <Card className="signin-card" sx={{ p: 2 }}>
          <img
            className="signin-logo"
            src={require("../../assets/Vision_Logo.svg").default}
            alt="logo"
          />
          {!emailSent ? (
            <>
              <Typography
                className="signin-text"
                variant="h5"
                sx={{ margin: "8px 0" }}
              >
                Forgot Password?
              </Typography>
              <Box
                sx={{
                  maxWidth: "100%",
                  mt: 3,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  width: 500,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontSize: "16px", fontWeight: 400, textAlign: "left" }}
                >
                  Enter the email or username associated with your account. If
                  the account exists, password reset instructions will be sent
                  by email.
                </Typography>
                <TextField
                  className="signin-username"
                  margin="normal"
                  id="username"
                  label="Username"
                  variant="outlined"
                  fullWidth
                  value={username}
                  onChange={onUsernameChange}
                  error={usernameError}
                  helperText={usernameError ? "This field can't be empty" : ""}
                />
                <Button
                  disabled={showLoading}
                  variant="contained"
                  onClick={handleResetPassword}
                  sx={{ width: "100%" }}
                >
                  Reset Password
                </Button>
                <Button
                  variant="text"
                  sx={{
                    mt: 0,
                    textTransform: "none",
                    alignSelf: "flex-start",
                    px: 0,
                    fontWeight: "550",
                  }}
                  onClick={() => navigate("/signin")}
                >
                  BACK TO SIGN IN
                </Button>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                maxWidth: "100%",
                display: "flex",
                flexDirection: "column",
                width: 500,
              }}
            >
              <Typography className="signin-text" variant="h6" sx={{ mt: 1 }}>
                Check Your Email
              </Typography>
              <Typography variant="subtitle1" sx={{ mt: 1 }}>
                We’ve Sent Password Reset Instructions
              </Typography>
              <Box sx={{ mt: 2, width: 500, maxWidth: "100%" }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, textAlign: "left", ml: 2 }}
                >
                  Next Steps
                </Typography>
                <ul style={{ marginTop: 8, marginBottom: 16 }}>
                  <li style={{ textAlign: "left" }}>
                    <Typography variant="body1">
                      Click the link in the email to set a new password
                    </Typography>
                  </li>
                  <li style={{ textAlign: "left" }}>
                    <Typography variant="body1">
                      For security, the link will expire in 15 minutes
                    </Typography>
                  </li>
                </ul>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, textAlign: "left", ml: 2 }}
                >
                  Didn’t Receive an Email?
                </Typography>
                <ul style={{ marginTop: 8, marginBottom: 16 }}>
                  <li style={{ textAlign: "left" }}>
                    <Typography variant="body1">
                      Check your spam and junk folders
                    </Typography>
                  </li>
                  <li style={{ textAlign: "left" }}>
                    <Typography variant="body1">
                      It may take a few minutes to arrive, so check again after
                      a short wait
                    </Typography>
                  </li>
                  <li style={{ textAlign: "left" }}>
                    <Typography variant="body1">
                      Make sure you entered the correct username
                    </Typography>
                  </li>
                  <li style={{ textAlign: "left" }}>
                    <Typography variant="body1">
                      Try re-sending the email with the button below
                    </Typography>
                  </li>
                </ul>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mt: 3,
                    justifyContent: "center",
                  }}
                >
                  <Button
                    variant="text"
                    sx={{
                      textTransform: "none",
                      px: 0,
                      flex: "1",
                      fontWeight: "550",
                    }}
                    onClick={() => navigate("/signin")}
                  >
                    BACK TO SIGN IN
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleResetPassword}
                    disabled={showLoading}
                    sx={{ flex: 2 }}
                  >
                    RE-SEND EMAIL
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Card>
      </Box>
      {showLoading && <Loading />}
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
      <Notification
        show={showSuccessNotification}
        message={successMessage}
        onClose={() => setShowSuccessNotification(false)}
      />
    </Box>
  );
}
